import {
  AlertTriangle,
  ExternalLink,
  MoreHorizontal,
  PauseCircle,
  PlayCircle,
  Settings2,
} from "lucide-react";
import { Link } from "react-router";

import { SortableTableHead, type SortDir } from "@/components/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AiServiceRow } from "@/lib/api";
import { formatDateTime, formatRelativeTime } from "@/lib/format";
import { formatAmount, SERVICE_LABEL, serviceNoun, SUSPENSION_SCOPE } from "@/lib/services-gateway";
import { cn } from "@/lib/utils";

import { hasWarning } from "./rows";

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

const STATUS: Record<AiServiceRow["status"], { label: string; variant: BadgeVariant }> = {
  active: { label: "Active", variant: "outline" },
  suspended: { label: "Suspended", variant: "destructive" },
  over_budget: { label: "Over budget", variant: "destructive" },
  budget_not_set: { label: "No budget set", variant: "warning" },
  no_key: { label: "No key", variant: "secondary" },
  unknown: { label: "Unknown", variant: "secondary" },
};

/** What a row's action asks for. */
export type RowAction =
  | { kind: "manage"; row: AiServiceRow }
  | { kind: "service"; row: AiServiceRow; suspend: boolean }
  | { kind: "project"; row: AiServiceRow; suspend: boolean };

interface ServicesTableProps {
  rows: AiServiceRow[];
  sortBy: string | null;
  sortDir: SortDir;
  onSort: (column: string) => void;
  onAction: (action: RowAction) => void;
}

/** Every gateway project × service this month, with its actions. */
export function ServicesTable({ rows, sortBy, sortDir, onSort, onAction }: ServicesTableProps) {
  if (rows.length === 0) {
    return <div className="text-muted-foreground py-12 text-center text-sm">No rows match.</div>;
  }
  const sortProps = { sortBy, sortDir, onSort };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead column="project" label="Project" {...sortProps} />
          <SortableTableHead column="service" label="Service" {...sortProps} />
          <SortableTableHead column="status" label="Status" {...sortProps} />
          <SortableTableHead column="used" label="This month" {...sortProps} />
          <SortableTableHead column="last_used" label="Last used" {...sortProps} />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <ServiceTableRow
            key={`${row.project_slug}-${row.service}`}
            row={row}
            onAction={onAction}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function ServiceTableRow({
  row,
  onAction,
}: {
  row: AiServiceRow;
  onAction: (action: RowAction) => void;
}) {
  const status = STATUS[row.status];
  return (
    <TableRow className={cn(row.suspended && "bg-destructive/5")}>
      <TableCell>
        <ProjectCell row={row} />
      </TableCell>
      <TableCell className="text-sm">
        {SERVICE_LABEL[row.service]}
        {row.off_gateway && (
          <p className="text-muted-foreground text-xs">
            {row.service === "llm" ? "models direct: key unused" : "own Langfuse: key unused"}
          </p>
        )}
      </TableCell>
      <TableCell className="max-w-xs whitespace-normal">
        <Badge variant={status.variant}>
          {row.suspended && <PauseCircle className="size-3" />}
          {status.label}
        </Badge>
        {row.suspended && row.suspended_reason && (
          <p className="text-muted-foreground mt-1 text-xs">
            {row.suspended_scope ? `${SUSPENSION_SCOPE[row.suspended_scope]}: ` : ""}
            <bdi>{row.suspended_reason}</bdi>
            {row.off_gateway && " (not in effect: the app calls it without the gateway)"}
          </p>
        )}
        <Warnings row={row} />
      </TableCell>
      <TableCell>
        <UsageCell row={row} />
      </TableCell>
      <TableCell
        className="text-muted-foreground text-sm"
        title={row.last_used_at ? formatDateTime(row.last_used_at) : undefined}
      >
        {formatRelativeTime(row.last_used_at ?? null)}
      </TableCell>
      <TableCell>
        <RowActions row={row} onAction={onAction} />
      </TableCell>
    </TableRow>
  );
}

function ProjectCell({ row }: { row: AiServiceRow }) {
  return (
    <div className="min-w-0">
      {row.project_id != null ? (
        <Link
          to={`/home?project=${row.project_id}&tab=details`}
          className="text-foreground hover:text-primary truncate text-sm font-medium"
        >
          {row.project_name}
        </Link>
      ) : (
        <p className="text-foreground truncate text-sm font-medium">{row.project_name}</p>
      )}
      <p className="text-muted-foreground truncate text-xs">
        {row.project_id != null ? (row.owner_name ?? row.owner_email ?? row.project_slug) : null}
        {row.project_id == null && "not a Scaffold UI project (keys left behind)"}
      </p>
    </div>
  );
}

/** Used of the budget: the amount, a meter and the share in words (never colour alone). */
function UsageCell({ row }: { row: AiServiceRow }) {
  const used = (row.unit === "usd" ? row.month_usd : row.month_requests) ?? 0;
  const budget = row.effective_budget ?? null;
  const percent = row.budget_used_percent ?? null;
  return (
    <div className="min-w-40 space-y-1">
      <p className="text-sm tabular-nums">
        {formatAmount(row.unit, used)}
        <span className="text-muted-foreground text-xs">
          {" "}
          of {budget === null ? "no budget" : formatAmount(row.unit, budget)}
        </span>
      </p>
      {budget !== null && budget > 0 && (
        <progress
          value={Math.min(used, budget)}
          max={budget}
          aria-label={`${row.project_name}, ${SERVICE_LABEL[row.service]}: share of the monthly budget used`}
          className={cn(
            "bg-muted h-1.5 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-transparent",
            row.over_budget
              ? "[&::-moz-progress-bar]:bg-destructive [&::-webkit-progress-value]:bg-destructive"
              : "[&::-moz-progress-bar]:bg-primary [&::-webkit-progress-value]:bg-primary",
          )}
        />
      )}
      {percent !== null && (
        <p
          className={cn(
            "text-xs tabular-nums",
            row.over_budget ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {percent.toFixed(1)} %{row.over_budget ? " (over budget)" : ""}
        </p>
      )}
      {row.unit === "usd" && row.month_input_tokens != null && (
        <p className="text-muted-foreground text-xs tabular-nums">
          {(row.month_input_tokens + (row.month_output_tokens ?? 0)).toLocaleString("en-GB")} tokens
        </p>
      )}
    </div>
  );
}

function Warnings({ row }: { row: AiServiceRow }) {
  if (!hasWarning(row)) {
    return null;
  }
  const lines = [
    row.warning,
    row.foundry_access_overdue && "The app's direct Foundry role is overdue for removal.",
    row.month_unpriced && "Some use has no price yet (counts as over budget).",
  ].filter((line): line is string => typeof line === "string" && line !== "");
  return (
    <>
      {lines.map((line) => (
        <p key={line} className="text-destructive mt-1 flex items-start gap-1 text-xs">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          {line}
        </p>
      ))}
    </>
  );
}

function RowActions({
  row,
  onAction,
}: {
  row: AiServiceRow;
  onAction: (action: RowAction) => void;
}) {
  if (row.project_id == null) {
    // Only the kill switch reaches a project Scaffold UI does not know.
    return null;
  }
  const service = serviceNoun(row.service);
  const ownSuspension = row.service_suspension != null;
  const projectSuspension = row.project_suspension != null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8">
          <MoreHorizontal className="size-4" />
          <span className="sr-only">Actions for {row.project_name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onAction({ kind: "manage", row })}>
          <Settings2 className="mr-2 size-4" /> Keys, limits and mode…
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/home?project=${row.project_id}&tab=details`}>
            <ExternalLink className="mr-2 size-4" /> Open project
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant={ownSuspension ? "default" : "destructive"}
          // Suspending a key the app does not use would stop nothing (the API refuses it).
          disabled={!ownSuspension && Boolean(row.off_gateway)}
          onClick={() => onAction({ kind: "service", row, suspend: !ownSuspension })}
        >
          {ownSuspension ? (
            <PlayCircle className="mr-2 size-4" />
          ) : (
            <PauseCircle className="mr-2 size-4" />
          )}
          {ownSuspension ? `Resume ${service}` : `Suspend ${service}`}
        </DropdownMenuItem>
        <DropdownMenuItem
          variant={projectSuspension ? "default" : "destructive"}
          onClick={() => onAction({ kind: "project", row, suspend: !projectSuspension })}
        >
          {projectSuspension ? (
            <PlayCircle className="mr-2 size-4" />
          ) : (
            <PauseCircle className="mr-2 size-4" />
          )}
          {projectSuspension ? "Resume the whole project" : "Suspend the whole project"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
